import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Buildings, MapPin, Phone, Envelope, Globe, Star,
  ShieldCheck, Export, User, LinkSimple, Warning, Brain, Lightning, Target, Lightbulb,
  House, Sun, Image as ImageIcon
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  municipal: 'Municipal', private: 'Private', sports_complex: 'Sports Complex',
  aquatic_center: 'Aquatic Center', swim_club: 'Swim Club', school: 'School',
  wellness: 'Wellness', hotel_pool: 'Hotel Pool'
};

function ScoreBadge({ score }) {
  const color = score >= 70 ? 'text-emerald-600 bg-emerald-50' : score >= 40 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
  return <span className={`font-data text-lg font-bold px-3 py-1 rounded-sm ${color}`}>{score}</span>;
}

export default function FacilityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  const fetchFacility = () => {
    fetch(`${API}/facilities/${id}`, { credentials: 'include' })
      .then(r => r.json())
      .then(setFacility)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchFacility(); }, [id]);

  const handleExport = async () => {
    await fetch(`${API}/export/start`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ facility_ids: [id] })
    });
    setFacility(prev => ({ ...prev, export_status: 'exporting' }));
  };

  const handleEnrich = async () => {
    setEnriching(true);
    setEnrichResult(null);
    try {
      const res = await fetch(`${API}/enrich/facility/${id}`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      setEnrichResult({ job_id: data.job_id, status: 'started' });
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const jr = await fetch(`${API}/enrich/jobs/${data.job_id}`, { credentials: 'include' });
        const job = await jr.json();
        if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(pollInterval);
          setEnriching(false);
          setEnrichResult(job);
          if (job.status === 'completed') {
            fetchFacility(); // Reload facility data with enriched info
          }
        } else {
          setEnrichResult(job);
        }
      }, 3000);
    } catch {
      setEnriching(false);
      setEnrichResult({ status: 'failed', error: 'Failed to start enrichment' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!facility) {
    return <div className="text-center py-12 text-zinc-500">Facility not found</div>;
  }

  const f = facility;
  const profileMeta = f._profile || {};
  const usesDefault = profileMeta.uses_default_facility_filters !== false;
  const profileDims = profileMeta.facility_dimensions || [];

  return (
    <div className="animate-fade-in" data-testid="facility-detail-page">
      {/* Header */}
      <button
        data-testid="back-to-facilities"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-800 mb-4 transition-colors"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="border border-zinc-200 bg-white p-6 mb-6">
        {/* Image Gallery */}
        {f.images && f.images.length > 0 && (
          <div className="mb-5 -mt-1" data-testid="facility-images">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {f.images.map((img, i) => (
                <img key={i} src={img} alt={`${f.name} ${i+1}`} className="h-32 w-auto rounded-sm object-cover border border-zinc-200 shrink-0" />
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl font-bold tracking-tight">{f.name}</h1>
              <Badge className={`rounded-full px-2 py-0.5 text-xs font-data ${f.verification_status === 'verified' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                {f.verification_status}
              </Badge>
              {usesDefault && f.pool_category && f.pool_category !== 'unknown' && (
                <Badge className={`rounded-full px-2 py-0.5 text-xs font-data flex items-center gap-1 ${f.pool_category === 'indoor' ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-800'}`}>
                  {f.pool_category === 'indoor' ? <House size={12} /> : <Sun size={12} />}
                  {f.pool_category === 'indoor' ? 'Overdekt' : 'Openlucht'}
                </Badge>
              )}
              {!usesDefault && profileDims.map(d => {
                const v = (f.dimensions || {})[d.key];
                if (!v) return null;
                const opt = (d.options || []).find(o => o.value === v);
                return (
                  <Badge
                    key={d.key}
                    data-testid={`facility-dim-${d.key}`}
                    className="rounded-full px-2 py-0.5 text-xs font-data bg-indigo-100 text-indigo-800"
                    title={d.label}
                  >
                    {opt?.label || v}
                  </Badge>
                );
              })}
            </div>
            {f.alt_name && <p className="text-sm text-zinc-500 mb-2">{f.alt_name}</p>}
            <div className="flex flex-wrap gap-4 text-sm text-zinc-600">
              <span className="flex items-center gap-1.5"><MapPin size={14} />{f.address}, {f.postcode} {f.city}</span>
              <span className="flex items-center gap-1.5"><Buildings size={14} />{f.region}, {f.country_code}</span>
              {f.phone && <span className="flex items-center gap-1.5"><Phone size={14} />{f.phone}</span>}
              {f.general_email && <span className="flex items-center gap-1.5"><Envelope size={14} />{f.general_email}</span>}
              {f.website && <a href={f.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#0055FF] hover:underline"><Globe size={14} />{f.domain || 'Website'}</a>}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-xs text-zinc-500 mb-1">Lead Score</div>
              <ScoreBadge score={f.lead_score} />
            </div>
            <div className="flex flex-col gap-2">
              {f.export_eligible && f.export_status !== 'exported' && (
                <button data-testid="export-facility-btn" onClick={handleExport} className="px-4 py-2 text-xs font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors flex items-center gap-1.5">
                  <Export size={14} /> Export to Exact
                </button>
              )}
              <button
                data-testid="enrich-facility-btn"
                onClick={handleEnrich}
                disabled={enriching}
                className="px-4 py-2 text-xs font-medium bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-50 transition-colors flex items-center gap-1.5"
              >
                <Brain size={14} weight="bold" /> {enriching ? 'Verrijken...' : f.enrichment_status === 'enriched' ? 'Opnieuw verrijken' : 'Quick Enrich'}
              </button>
            </div>
          </div>
        </div>

        {/* Meta info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-6" data-testid="facility-meta-grid">
          {(usesDefault ? [
            { label: 'Type', value: TYPE_LABELS[f.facility_type] || f.facility_type },
            { label: 'Pool', value: f.pool_category === 'indoor' ? 'Overdekt' : f.pool_category === 'outdoor' ? 'Openlucht' : 'Onbekend' },
            { label: 'Operator', value: f.operator || '-' },
            { label: 'Legal Entity', value: f.legal_entity || '-' },
            { label: 'Pool Size', value: f.pool_size || '-' },
            { label: 'Public', value: f.is_public ? 'Yes' : 'No' },
            { label: 'Municipal', value: f.is_municipal ? 'Yes' : 'No' },
            { label: 'Source Reliability', value: f.source_reliability ? `${(f.source_reliability * 100).toFixed(0)}%` : '-' },
          ] : [
            // Profile-specific dimensions first
            ...profileDims.map(d => {
              const v = (f.dimensions || {})[d.key];
              const opt = (d.options || []).find(o => o.value === v);
              return { label: d.label, value: opt?.label || v || '— niet geclassificeerd —' };
            }),
            { label: 'Operator', value: f.operator || '-' },
            { label: 'Legal Entity', value: f.legal_entity || '-' },
            { label: 'Source Reliability', value: f.source_reliability ? `${(f.source_reliability * 100).toFixed(0)}%` : '-' },
          ]).map((m, i) => (
            <div key={i} className="bg-white p-3">
              <div className="text-xs text-zinc-500 mb-0.5">{m.label}</div>
              <div className="text-sm font-medium">{m.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Enrichment Progress */}
      {enrichResult && enrichResult.status !== 'completed' && enrichResult.status !== 'failed' && (
        <div className="border border-[#0055FF] bg-blue-50 p-4 mb-6" data-testid="enrichment-progress">
          <div className="flex items-center gap-3">
            <Brain size={20} weight="bold" className="text-[#0055FF] animate-pulse" />
            <div className="flex-1">
              <div className="text-sm font-medium text-[#0055FF]">Quick Enrich — Verrijking bezig</div>
              <div className="text-xs text-zinc-600">{enrichResult.current_step || 'Processing...'}</div>
            </div>
            <span className="font-data text-sm font-bold text-[#0055FF]">{enrichResult.progress || 0}%</span>
          </div>
        </div>
      )}

      {/* AI Insights Panel */}
      {f.ai_insights && !f.ai_insights.error && (
        <div className="border border-zinc-200 bg-white p-5 mb-6" data-testid="ai-insights-panel">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={20} weight="bold" className="text-[#0055FF]" />
            <h3 className="font-heading text-lg font-semibold">AI Commercial Insights</h3>
            <Badge className="rounded-full px-2 py-0.5 text-xs font-data bg-blue-100 text-blue-800">GPT-5.2</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {f.ai_insights.estimated_annual_budget && (
              <div className="border border-zinc-100 p-3 rounded-sm">
                <div className="text-xs text-zinc-500 mb-1">Est. Annual Budget</div>
                <div className="text-sm font-medium">{f.ai_insights.estimated_annual_budget}</div>
              </div>
            )}
            {f.ai_insights.best_approach && (
              <div className="border border-zinc-100 p-3 rounded-sm">
                <div className="text-xs text-zinc-500 mb-1">Best Approach</div>
                <div className="text-sm">{f.ai_insights.best_approach}</div>
              </div>
            )}
            {f.ai_insights.decision_timeline && (
              <div className="border border-zinc-100 p-3 rounded-sm">
                <div className="text-xs text-zinc-500 mb-1">Decision Timeline</div>
                <div className="text-sm">{f.ai_insights.decision_timeline}</div>
              </div>
            )}
            {f.ai_insights.overall_opportunity_score && (
              <div className="border border-zinc-100 p-3 rounded-sm">
                <div className="text-xs text-zinc-500 mb-1">Opportunity Score</div>
                <div className={`font-data text-xl font-bold ${f.ai_insights.overall_opportunity_score >= 70 ? 'text-emerald-600' : f.ai_insights.overall_opportunity_score >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                  {f.ai_insights.overall_opportunity_score}
                </div>
              </div>
            )}
          </div>
          {f.ai_insights.likely_needs && f.ai_insights.likely_needs.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-zinc-500 mb-2">Likely Needs</div>
              <div className="flex flex-wrap gap-2">
                {f.ai_insights.likely_needs.map((need, i) => (
                  <Badge key={i} className="rounded-full px-3 py-1 text-xs font-data bg-zinc-100 text-zinc-700">{need}</Badge>
                ))}
              </div>
            </div>
          )}
          {f.ai_insights.key_selling_points && f.ai_insights.key_selling_points.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-zinc-500 mb-2">Key Selling Points</div>
              <div className="flex flex-wrap gap-2">
                {f.ai_insights.key_selling_points.map((point, i) => (
                  <Badge key={i} className="rounded-full px-3 py-1 text-xs font-data bg-emerald-50 text-emerald-700">{point}</Badge>
                ))}
              </div>
            </div>
          )}
          {f.inferred_email_pattern && (
            <div className="mt-4 border-t border-zinc-100 pt-3">
              <div className="flex items-center gap-2">
                <Envelope size={14} className="text-zinc-400" />
                <span className="text-xs text-zinc-500">Inferred Email Pattern:</span>
                <span className="font-data text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-sm">{f.inferred_email_pattern}</span>
                <Badge className="rounded-full px-1.5 py-0 text-[10px] font-data bg-amber-100 text-amber-800">inferred</Badge>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="contacts" className="border border-zinc-200 bg-white">
        <TabsList className="w-full justify-start border-b border-zinc-200 rounded-none bg-transparent p-0 h-auto">
          {['contacts', 'sources', 'compliance'].map(tab => (
            <TabsTrigger
              key={tab}
              value={tab}
              data-testid={`tab-${tab}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F172A] data-[state=active]:font-semibold data-[state=active]:shadow-none px-6 py-3 text-sm capitalize"
            >
              {tab === 'contacts' ? `Contacts (${f.contacts?.length || 0})` : tab === 'sources' ? `Sources (${f.sources?.length || 0})` : 'Compliance'}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="p-0 mt-0">
          {(!f.contacts || f.contacts.length === 0) ? (
            <div className="p-8 text-center text-zinc-400 text-sm">No contacts found for this facility</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Name</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Role</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Email</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Confidence</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Category</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Decision</th>
                </tr>
              </thead>
              <tbody>
                {f.contacts.map(c => (
                  <tr key={c.contact_id} data-testid={`contact-row-${c.contact_id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-zinc-400" />
                        <div>
                          <div className="font-medium">{c.full_name}</div>
                          <div className="text-xs text-zinc-400">{c.organization}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{c.role_title}</td>
                    <td className="px-4 py-3">
                      {c.email && (
                        <div className="flex items-center gap-1.5">
                          <span className="font-data text-xs">{c.email}</span>
                          <Badge className={`rounded-full px-1.5 py-0 text-[10px] font-data ${c.email_status === 'verified' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            {c.email_status}
                          </Badge>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-data text-sm font-bold ${c.confidence_score >= 0.8 ? 'text-emerald-600' : c.confidence_score >= 0.6 ? 'text-amber-600' : 'text-red-500'}`}>
                        {(c.confidence_score * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className="rounded-full px-2 py-0.5 text-xs font-data bg-zinc-100 text-zinc-700">{c.role_category}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-600">{c.decision_level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TabsContent>

        {/* Sources Tab */}
        <TabsContent value="sources" className="p-0 mt-0">
          {(!f.sources || f.sources.length === 0) ? (
            <div className="p-8 text-center text-zinc-400 text-sm">No source records</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Source URL</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Type</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Language</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Last Crawled</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Data Points</th>
                </tr>
              </thead>
              <tbody>
                {f.sources.map(s => (
                  <tr key={s.source_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline flex items-center gap-1.5 font-data text-xs">
                        <LinkSimple size={14} />{s.url}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className="rounded-full px-2 py-0.5 text-xs font-data bg-zinc-100 text-zinc-700">{s.source_type}</Badge>
                    </td>
                    <td className="px-4 py-3 font-data text-xs uppercase">{s.language}</td>
                    <td className="px-4 py-3 font-data text-xs">{s.last_crawled ? new Date(s.last_crawled).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 font-data text-sm font-bold">{s.data_points_extracted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="p-0 mt-0">
          {(!f.compliance_flags || f.compliance_flags.length === 0) ? (
            <div className="p-8 text-center text-sm">
              <ShieldCheck size={32} className="text-emerald-500 mx-auto mb-2" />
              <span className="text-emerald-600 font-medium">No compliance issues</span>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {f.compliance_flags.map(cf => (
                <div key={cf.flag_id} className="flex items-start gap-3 p-4">
                  <Warning size={18} className={cf.severity === 'error' ? 'text-red-500' : 'text-amber-500'} />
                  <div>
                    <div className="text-sm font-medium">{cf.flag_type.replace(/_/g, ' ')}</div>
                    <div className="text-xs text-zinc-500">{cf.message}</div>
                  </div>
                  <Badge className={`ml-auto rounded-full px-2 py-0.5 text-xs font-data ${cf.severity === 'error' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                    {cf.severity}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
